// ABOUTME: Admin page for viewing email domain clusters and organic adoption patterns.
// ABOUTME: Shows sortable table of business domains with user counts and conversion potential.

import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, Search, Download, Loader2 } from 'lucide-react';
import { adminApi } from '@/services/api/client';
import type { DomainCluster } from '@/types/api';

export function DomainClustersPage() {
  const [clusters, setClusters] = useState<DomainCluster[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [minUsers, setMinUsers] = useState(2);
  const [search, setSearch] = useState('');
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof DomainCluster>('conversion_potential');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const fetchClusters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.getDomainClusters({
        min_users: minUsers,
        search: search || undefined,
      });
      setClusters(result.clusters);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to fetch domain clusters:', err);
      setError('Failed to load domain clusters');
    } finally {
      setLoading(false);
    }
  }, [minUsers, search]);

  useEffect(() => {
    fetchClusters();
  }, [fetchClusters]);

  const handleSort = (field: keyof DomainCluster) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedClusters = [...clusters].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    }
    return sortDir === 'asc'
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const handleExportCsv = () => {
    const headers = ['Domain', 'Total Users', 'Workspaces', 'Paid Users', 'Active Users', 'Conversion Potential'];
    const rows = clusters.map(c => [
      c.domain, c.total_users, c.total_workspaces, c.paid_users, c.active_users, c.conversion_potential,
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `domain-clusters-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Organic Adoption Clusters</h1>
          <p className="text-sm text-gray-500 mt-1">
            {total} clusters found across business email domains
          </p>
        </div>
        <button
          onClick={handleExportCsv}
          disabled={clusters.length === 0}
          className="flex items-center gap-2 px-3 py-2 text-sm border rounded-sm hover:bg-gray-50 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search domains..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Min users:</label>
          <input
            type="range"
            min={1}
            max={20}
            value={minUsers}
            onChange={(e) => setMinUsers(Number(e.target.value))}
            className="w-24"
          />
          <span className="text-sm font-medium text-gray-700 w-6">{minUsers}</span>
        </div>
      </div>

      {/* Loading / Error / Table */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading clusters...
        </div>
      )}

      {error && (
        <div className="text-center py-12 text-red-600">
          <p>{error}</p>
          <button
            onClick={fetchClusters}
            className="mt-2 text-sm text-blue-600 hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        <div className="border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="w-8 px-3 py-2"></th>
                {[
                  { key: 'domain', label: 'Domain' },
                  { key: 'total_users', label: 'Users' },
                  { key: 'total_workspaces', label: 'Workspaces' },
                  { key: 'paid_users', label: 'Paid' },
                  { key: 'active_users', label: 'Active (30d)' },
                  { key: 'conversion_potential', label: 'Score' },
                ].map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key as keyof DomainCluster)}
                    className="px-3 py-2 text-left font-medium text-gray-600 cursor-pointer hover:text-gray-900"
                  >
                    {col.label}
                    {sortField === col.key && (sortDir === 'asc' ? ' ^' : ' v')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedClusters.map(cluster => (
                <tr
                  key={cluster.domain}
                  onClick={() => setExpandedDomain(
                    expandedDomain === cluster.domain ? null : cluster.domain
                  )}
                  className="border-b hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-3 py-2">
                    {expandedDomain === cluster.domain ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </td>
                  <td className="px-3 py-2 font-medium">{cluster.domain}</td>
                  <td className="px-3 py-2">{cluster.total_users}</td>
                  <td className="px-3 py-2">{cluster.total_workspaces}</td>
                  <td className="px-3 py-2">{cluster.paid_users}</td>
                  <td className="px-3 py-2">{cluster.active_users}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      cluster.conversion_potential >= 15
                        ? 'bg-green-100 text-green-700'
                        : cluster.conversion_potential >= 8
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {cluster.conversion_potential}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {sortedClusters.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No clusters found with the current filters.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
